import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useAuthStore } from '@/store/authStore';
import { useResetScrollOnStepChange } from '@/hooks/useResetScrollOnStepChange';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ProgressIndicator } from './ProgressIndicator';
import { SelectAccountStep } from './steps/SelectAccountStep';
import { CampaignBasicsStep, type CampaignType, type CampaignStatus } from './steps/CampaignBasicsStep';
import { BudgetBiddingStep, type BiddingStrategy } from './steps/BudgetBiddingStep';
import { NetworkSettingsStep, type NetworkSettings, type TargetingSettings } from './steps/NetworkSettingsStep';
import { ShoppingSettingsStep, type ShoppingSettings } from './steps/ShoppingSettingsStep';
import { DemandGenSettingsStep } from './steps/DemandGenSettingsStep';
import { type DemandGenSettings, DEFAULT_DEMAND_GEN_SETTINGS } from './steps/demandGenSettings';
import { ScheduleStep } from './steps/ScheduleStep';
import { ReviewStep } from './steps/ReviewStep';
import apiClient from '@/services/api/client';
import { debug } from '@/utils/debug';
import { dollarsToMicros } from '@/utils/googleAds';
import { formatDateForInput } from '@/utils/format';

interface CreateCampaignWizardProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void | Promise<void>;
  /**
   * When set, the wizard initializes with this Google Ads account pre-selected
   * and starts at Step 2 — Step 1's `SelectAccountStep` is bypassed for this
   * invocation. The Account page's launcher passes this after the user picks
   * from `OmniAccountPicker`. Unset = wizard falls back to its standalone
   * behavior (Step 1 always rendered, with the single linked account auto-
   * selected via the existing useEffect).
   */
  preSelectedAccountId?: string | null;
}

interface FormData {
  selectedAccountId: string | null;
  campaignName: string;
  campaignType: CampaignType;
  status: CampaignStatus;
  containsEuPoliticalAdvertising: boolean;
  enableAiMax: boolean;
  dailyBudget: string;
  biddingStrategy: BiddingStrategy;
  targetCpaValue: string;
  targetRoasValue: string;
  targetImpressionShareLocation: string;
  targetImpressionShareLocationFraction: string;
  targetImpressionShareCpcCeiling: string;
  networkSettings: NetworkSettings;
  targetingSettings: TargetingSettings;
  shoppingSettings: ShoppingSettings;
  demandGenSettings: DemandGenSettings;
  startDate: string;
  endDate: string;
  hasEndDate: boolean;
}

interface FormErrors {
  account?: string;
  name?: string;
  budget?: string;
  targetCpa?: string;
  targetRoas?: string;
  targetImpressionShareLocationFraction?: string;
  targetImpressionShareCpcCeiling?: string;
  merchantId?: string;
  salesCountry?: string;
  startDate?: string;
  endDate?: string;
}

const WIZARD_STEPS = [
  { label: 'Account', description: '' },
  { label: 'Basics', description: '' },
  { label: 'Budget & Bidding', description: '' },
  { label: 'Targeting', description: '' },
  { label: 'Schedule', description: '' },
  { label: 'Review', description: '' },
];

export const CreateCampaignWizard: React.FC<CreateCampaignWizardProps> = ({
  isOpen,
  onClose,
  onSuccess,
  preSelectedAccountId = null,
}) => {
  const { linkedAccountDetails } = useAuthStore();
  const [currentStep, setCurrentStep] = useState(1);
  const scrollBodyRef = useRef<HTMLDivElement>(null);
  useResetScrollOnStepChange(currentStep, scrollBodyRef);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Memoized per open/close cycle (not per render) so a user keeping the
  // wizard open across midnight doesn't trip the reset-on-open effect's
  // dependency on `today` and wipe their in-progress edits. `isOpen` is
  // intentionally a recompute trigger here, not a closed-over value —
  // exhaustive-deps doesn't grok that intent.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const today = useMemo(() => formatDateForInput(new Date()), [isOpen]);

  const [formData, setFormData] = useState<FormData>({
    selectedAccountId: null,
    campaignName: '',
    campaignType: 'SEARCH',
    status: 'PAUSED',
    containsEuPoliticalAdvertising: false,
    enableAiMax: false,
    dailyBudget: '',
    biddingStrategy: 'MANUAL_CPC',
    targetCpaValue: '',
    targetRoasValue: '',
    targetImpressionShareLocation: 'ANYWHERE_ON_PAGE',
    targetImpressionShareLocationFraction: '',
    targetImpressionShareCpcCeiling: '',
    networkSettings: {
      targetGoogleSearch: true,
      targetSearchNetwork: true,
      targetContentNetwork: false,
    },
    targetingSettings: {
      geoTargetIds: [2840], // Default to USA
      languageIds: [1000], // Default to English
    },
    shoppingSettings: {
      merchantId: '',
      salesCountry: '',
      campaignPriority: 0,
      enableLocal: false,
    },
    demandGenSettings: DEFAULT_DEMAND_GEN_SETTINGS,
    startDate: today,
    endDate: '',
    hasEndDate: false,
  });

  const [errors, setErrors] = useState<FormErrors>({});

  // Auto-select account if only one is available, OR if the launcher passed
  // a preSelectedAccountId (omni picker selection takes precedence over the
  // single-account fallback).
  useEffect(() => {
    const resolvedId =
      preSelectedAccountId ??
      (linkedAccountDetails.length === 1 ? linkedAccountDetails[0].id : null);
    if (resolvedId && !formData.selectedAccountId) {
      setFormData((prev) => ({
        ...prev,
        selectedAccountId: resolvedId,
      }));
    }
    // formData.selectedAccountId intentionally omitted from deps to prevent re-running
    // when user manually changes selection. We only want to auto-select when accounts change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedAccountDetails, preSelectedAccountId]);

  // Reset form when wizard opens/closes. When preSelectedAccountId is set,
  // Step 1's SelectAccountStep is bypassed (currentStep starts at 2).
  useEffect(() => {
    if (isOpen) {
      const resolvedId =
        preSelectedAccountId ??
        (linkedAccountDetails.length === 1 ? linkedAccountDetails[0].id : null);
      // Bypass Step 1 only when the launcher pre-selected an account.
      // (Auto-selection from a single linked account leaves Step 1 rendered
      // for visual confirmation — that's Google's existing behavior and we
      // preserve it for the standalone-invocation case.)
      setCurrentStep(preSelectedAccountId ? 2 : 1);
      setErrors({});
      setSubmitError(null);
      // Reset to defaults except account (keep auto-selected / pre-selected account)
      setFormData({
        selectedAccountId: resolvedId,
        campaignName: '',
        campaignType: 'SEARCH',
        status: 'PAUSED',
        containsEuPoliticalAdvertising: false,
        dailyBudget: '',
        biddingStrategy: 'MANUAL_CPC',
        targetCpaValue: '',
        targetRoasValue: '',
        targetImpressionShareLocation: 'ANYWHERE_ON_PAGE',
        targetImpressionShareLocationFraction: '',
        targetImpressionShareCpcCeiling: '',
        enableAiMax: false,
        networkSettings: {
          targetGoogleSearch: true,
          targetSearchNetwork: true,
          targetContentNetwork: false,
        },
        targetingSettings: {
          geoTargetIds: [2840], // Default to USA
          languageIds: [1000], // Default to English
        },
        shoppingSettings: {
          merchantId: '',
          salesCountry: '',
          campaignPriority: 0,
          enableLocal: false,
        },
        demandGenSettings: DEFAULT_DEMAND_GEN_SETTINGS,
        startDate: today,
        endDate: '',
        hasEndDate: false,
      });
    }
  }, [isOpen, linkedAccountDetails, today, preSelectedAccountId]);

  // Validation function for each step
  const validateStep = (step: number): boolean => {
    const newErrors: FormErrors = {};

    switch (step) {
      case 1: // Account selection
        if (!formData.selectedAccountId) {
          newErrors.account = 'Please select a Google Ads account';
        }
        break;

      case 2: // Campaign basics
        if (!formData.campaignName.trim()) {
          newErrors.name = 'Campaign name is required';
        } else if (formData.campaignName.length > 255) {
          newErrors.name = 'Campaign name must be 255 characters or less';
        }
        break;

      case 3: { // Budget & Bidding
        const budget = parseFloat(formData.dailyBudget);
        if (!formData.dailyBudget || isNaN(budget)) {
          newErrors.budget = 'Daily budget is required';
        } else if (budget <= 0) {
          newErrors.budget = 'Daily budget must be greater than $0';
        } else if (budget > 1000000) {
          newErrors.budget = 'Maximum daily budget is $1,000,000';
        }

        // Validate Target CPA if provided
        if (formData.biddingStrategy === 'TARGET_CPA' && formData.targetCpaValue) {
          const targetCpa = parseFloat(formData.targetCpaValue);
          if (isNaN(targetCpa)) {
            newErrors.targetCpa = 'Target CPA must be a valid number';
          } else if (targetCpa <= 0) {
            newErrors.targetCpa = 'Target CPA must be greater than $0';
          } else if (targetCpa > 1000000) {
            newErrors.targetCpa = 'Maximum target CPA is $1,000,000';
          }
        }

        // Validate Target ROAS if provided
        if (formData.biddingStrategy === 'TARGET_ROAS' && formData.targetRoasValue) {
          const targetRoas = parseFloat(formData.targetRoasValue);
          if (isNaN(targetRoas)) {
            newErrors.targetRoas = 'Target ROAS must be a valid number';
          } else if (targetRoas <= 0) {
            newErrors.targetRoas = 'Target ROAS must be greater than 0';
          } else if (targetRoas > 1000) {
            newErrors.targetRoas = 'Maximum target ROAS is 1000x';
          }
        }

        // Validate Target Impression Share settings
        if (formData.biddingStrategy === 'TARGET_IMPRESSION_SHARE') {
          // Location fraction is required
          if (!formData.targetImpressionShareLocationFraction) {
            newErrors.targetImpressionShareLocationFraction = 'Target impression share % is required';
          } else {
            const fraction = parseFloat(formData.targetImpressionShareLocationFraction);
            if (isNaN(fraction)) {
              newErrors.targetImpressionShareLocationFraction = 'Must be a valid number';
            } else if (fraction < 1 || fraction > 100) {
              newErrors.targetImpressionShareLocationFraction = 'Must be between 1 and 100';
            }
          }

          // CPC ceiling is optional but must be valid if provided
          if (formData.targetImpressionShareCpcCeiling) {
            const ceiling = parseFloat(formData.targetImpressionShareCpcCeiling);
            if (isNaN(ceiling)) {
              newErrors.targetImpressionShareCpcCeiling = 'Max CPC must be a valid number';
            } else if (ceiling <= 0) {
              newErrors.targetImpressionShareCpcCeiling = 'Max CPC must be greater than $0';
            }
          }
        }
        break;
      }

      case 4: // Network settings (Search) or Shopping settings (Shopping)
        if (formData.campaignType === 'SHOPPING') {
          // Validate Shopping settings
          if (!formData.shoppingSettings.merchantId.trim()) {
            newErrors.merchantId = 'Merchant Center ID is required';
          } else if (!/^\d+$/.test(formData.shoppingSettings.merchantId)) {
            newErrors.merchantId = 'Merchant Center ID must be a number';
          }

          if (!formData.shoppingSettings.salesCountry.trim()) {
            newErrors.salesCountry = 'Sales country is required';
          } else if (!/^[A-Z]{2}$/.test(formData.shoppingSettings.salesCountry)) {
            newErrors.salesCountry = 'Sales country must be a 2-letter country code (e.g., US)';
          }
        }
        // Network settings (Search campaigns) - no validation needed
        break;

      case 5: // Schedule
        if (!formData.startDate) {
          newErrors.startDate = 'Start date is required';
        } else if (new Date(formData.startDate) < new Date(today)) {
          newErrors.startDate = 'Start date cannot be in the past';
        }

        if (formData.hasEndDate) {
          if (!formData.endDate) {
            newErrors.endDate = 'End date is required when "Set an end date" is enabled';
          } else if (new Date(formData.endDate) <= new Date(formData.startDate)) {
            newErrors.endDate = 'End date must be after start date';
          }
        }
        break;

      case 6: // Review
        // Re-validate all steps
        return validateStep(1) && validateStep(2) && validateStep(3) && validateStep(5);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      // Skip network/shopping/demand gen settings step for campaigns that don't need them
      // SEARCH campaigns use network settings, SHOPPING has shopping settings, DEMAND_GEN has demand gen settings
      if (currentStep === 3 && formData.campaignType !== 'SEARCH' && formData.campaignType !== 'SHOPPING' && formData.campaignType !== 'DEMAND_GEN') {
        setCurrentStep(5);
      } else {
        setCurrentStep((prev) => Math.min(prev + 1, WIZARD_STEPS.length));
      }
    }
  };

  // Floor for backward navigation. The Account step (Step 1) is one-way:
  // once the user advances past it, they can't go back to change the
  // account — picking the wrong one means closing the wizard and starting
  // over. Changing the account mid-flow would invalidate every later step
  // (budget rules, available bidding strategies, etc.) so this is the
  // safe stance. Same policy applied to the Meta wizard's `previousFloor`.
  const previousFloor = 2;

  const handlePrevious = () => {
    // Skip network/shopping/demand gen settings step for campaigns that don't need them when going back
    // SEARCH campaigns use network settings, SHOPPING has shopping settings, DEMAND_GEN has demand gen settings
    if (currentStep === 5 && formData.campaignType !== 'SEARCH' && formData.campaignType !== 'SHOPPING' && formData.campaignType !== 'DEMAND_GEN') {
      setCurrentStep(3);
    } else {
      setCurrentStep((prev) => Math.max(prev - 1, previousFloor));
    }
  };

  const handleStepClick = (step: number) => {
    // Allow clicking on completed steps (but not below the floor when the
    // launcher pre-selected — that bypasses Step 1).
    if (step < currentStep && step >= previousFloor) {
      setCurrentStep(step);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep(6)) {
      debug.error('CreateCampaignWizard: Validation failed on final review');
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const payload = {
        google_ads_account_id: formData.selectedAccountId,
        campaign_name: formData.campaignName,
        campaign_type: formData.campaignType,
        status: formData.status,
        contains_eu_political_advertising: formData.containsEuPoliticalAdvertising,
        daily_budget_micros: dollarsToMicros(formData.dailyBudget),
        bidding_strategy: formData.biddingStrategy,
        // v22 enhancements: AI Max for Search (enabled via checkbox when using Smart Bidding)
        ...(formData.campaignType === 'SEARCH' && formData.enableAiMax && { enable_ai_max: true }),
        ...(formData.targetCpaValue && {
          target_cpa_micros: dollarsToMicros(formData.targetCpaValue),
        }),
        ...(formData.targetRoasValue && {
          target_roas: parseFloat(formData.targetRoasValue),
        }),
        // v22 enhancements: Target Impression Share settings
        ...(formData.biddingStrategy === 'TARGET_IMPRESSION_SHARE' && {
          target_impression_share_location: formData.targetImpressionShareLocation,
          target_impression_share_location_fraction_micros: formData.targetImpressionShareLocationFraction
            ? Math.round(parseFloat(formData.targetImpressionShareLocationFraction) * 10000) // Input is expected as a percentage (1-100), not a decimal fraction (0.01-1.0). Converts percentage to micros (e.g., 75% -> 750000).
            : undefined,
          ...(formData.targetImpressionShareCpcCeiling && {
            target_impression_share_cpc_ceiling_micros: dollarsToMicros(formData.targetImpressionShareCpcCeiling),
          }),
        }),
        ...(formData.campaignType === 'SEARCH' && {
          network_settings: {
            target_google_search: formData.networkSettings.targetGoogleSearch,
            target_search_network: formData.networkSettings.targetSearchNetwork,
            target_content_network: formData.networkSettings.targetContentNetwork,
          },
        }),
        // Geo and language targeting (defaults to USA/English if empty)
        ...(formData.targetingSettings.geoTargetIds.length > 0 && {
          geo_target_ids: formData.targetingSettings.geoTargetIds,
        }),
        ...(formData.targetingSettings.languageIds.length > 0 && {
          language_ids: formData.targetingSettings.languageIds,
        }),
        ...(formData.campaignType === 'SHOPPING' && {
          shopping_settings: {
            merchant_id: parseInt(formData.shoppingSettings.merchantId, 10),
            sales_country: formData.shoppingSettings.salesCountry,
            campaign_priority: formData.shoppingSettings.campaignPriority,
            enable_local: formData.shoppingSettings.enableLocal,
          },
        }),
        ...(formData.campaignType === 'DEMAND_GEN' && {
          demand_gen_settings: {
            channel_strategy: formData.demandGenSettings.channelSettings.channelStrategy,
            channels: {
              youtube_in_stream: formData.demandGenSettings.channelSettings.youtubeInStream,
              youtube_in_feed: formData.demandGenSettings.channelSettings.youtubeInFeed,
              youtube_shorts: formData.demandGenSettings.channelSettings.youtubeShorts,
              discover: formData.demandGenSettings.channelSettings.discover,
              gmail: formData.demandGenSettings.channelSettings.gmail,
              display: formData.demandGenSettings.channelSettings.display,
              // maps not yet supported in Google Ads API v22/v23
            },
            optimized_targeting: formData.demandGenSettings.optimizedTargeting,
          },
        }),
        start_date: formData.startDate,
        end_date: formData.hasEndDate ? formData.endDate : null,
      };

      debug.log('CreateCampaignWizard: Submitting campaign creation request', payload);

      const response = await apiClient.post('/campaigns/create', payload);

      debug.log('CreateCampaignWizard: Campaign created successfully', response.data);

      // Call success callback and wait for it to complete (e.g., refetch campaigns)
      await onSuccess();

      // Close the wizard after successful creation and data refresh
      onClose();
    } catch (error: unknown) {
      debug.error('CreateCampaignWizard: Failed to create campaign', error);

      // Extract error message from various possible response formats
      let errorMessage = 'Failed to create Google Ads campaign. Please try again.';

      const apiError = error as { response?: { data?: { detail?: string; message?: string; error?: string } }; message?: string };
      if (apiError.response?.data) {
        const data = apiError.response.data;
        // Try different possible error message fields
        errorMessage = data.detail || data.message || data.error || errorMessage;

        // If it's still an object, stringify it
        if (typeof errorMessage === 'object') {
          errorMessage = JSON.stringify(errorMessage);
        }
      } else if (apiError.message) {
        errorMessage = apiError.message;
      }

      setSubmitError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const selectedAccount = linkedAccountDetails.find((acc) => acc.id === formData.selectedAccountId) || null;

  // Confirm-on-cancel only when the user has touched at least one field.
  // Clean state (e.g. the wizard was opened and immediately X-closed)
  // skips the confirmation — no value in a "discard nothing?" prompt.
  // Tracking name + budget is sufficient — those are the two fields the
  // user MUST type into to make progress, so they're the signal that real
  // input has happened.
  const isDirty =
    formData.campaignName !== '' || formData.dailyBudget !== '';

  const handleRequestClose = () => {
    if (isDirty) {
      setShowCancelConfirm(true);
    } else {
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full my-4 sm:my-8 relative">
        {/* Close button */}
        <button
          onClick={handleRequestClose}
          disabled={isSubmitting}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full p-1 disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Close"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>

        {/* Header */}
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900 pr-8">Create New Google Ads Campaign</h2>
          <p className="text-sm text-gray-600 mt-1">
            Set up a new Google Ads campaign in {WIZARD_STEPS.length} simple steps
          </p>
        </div>

        {/* Progress Indicator */}
        <div className="px-4 sm:px-6 py-2 border-b border-gray-100">
          <ProgressIndicator
            currentStep={currentStep}
            totalSteps={WIZARD_STEPS.length}
            steps={WIZARD_STEPS}
            onStepClick={handleStepClick}
          />
        </div>

        {/* Step Content */}
        <div ref={scrollBodyRef} className="px-6 py-6 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 400px)', minHeight: '400px' }}>
          {currentStep === 1 && (
            <SelectAccountStep
              accounts={linkedAccountDetails}
              selectedAccountId={formData.selectedAccountId}
              onSelectAccount={(id) =>
                setFormData((prev) => ({ ...prev, selectedAccountId: id }))
              }
              error={errors.account}
            />
          )}

          {currentStep === 2 && (
            <CampaignBasicsStep
              campaignName={formData.campaignName}
              campaignType={formData.campaignType}
              status={formData.status}
              containsEuPoliticalAdvertising={formData.containsEuPoliticalAdvertising}
              onChangeName={(name) => setFormData((prev) => ({ ...prev, campaignName: name }))}
              onChangeType={(type) => setFormData((prev) => ({ ...prev, campaignType: type }))}
              onChangeStatus={(status) => setFormData((prev) => ({ ...prev, status }))}
              onChangeEuPoliticalAdvertising={(value) => setFormData((prev) => ({ ...prev, containsEuPoliticalAdvertising: value }))}
              errors={{ name: errors.name }}
            />
          )}

          {currentStep === 3 && (
            <BudgetBiddingStep
              campaignType={formData.campaignType}
              dailyBudget={formData.dailyBudget}
              biddingStrategy={formData.biddingStrategy}
              enableAiMax={formData.enableAiMax}
              targetCpaValue={formData.targetCpaValue}
              targetRoasValue={formData.targetRoasValue}
              targetImpressionShareLocation={formData.targetImpressionShareLocation}
              targetImpressionShareLocationFraction={formData.targetImpressionShareLocationFraction}
              targetImpressionShareCpcCeiling={formData.targetImpressionShareCpcCeiling}
              onChangeBudget={(budget) => setFormData((prev) => ({ ...prev, dailyBudget: budget }))}
              onChangeBiddingStrategy={(strategy) =>
                setFormData((prev) => ({ ...prev, biddingStrategy: strategy }))
              }
              onChangeEnableAiMax={(enabled) => setFormData((prev) => ({ ...prev, enableAiMax: enabled }))}
              onChangeTargetCpa={(value) => setFormData((prev) => ({ ...prev, targetCpaValue: value }))}
              onChangeTargetRoas={(value) => setFormData((prev) => ({ ...prev, targetRoasValue: value }))}
              onChangeTargetImpressionShareLocation={(value) => setFormData((prev) => ({ ...prev, targetImpressionShareLocation: value }))}
              onChangeTargetImpressionShareLocationFraction={(value) => setFormData((prev) => ({ ...prev, targetImpressionShareLocationFraction: value }))}
              onChangeTargetImpressionShareCpcCeiling={(value) => setFormData((prev) => ({ ...prev, targetImpressionShareCpcCeiling: value }))}
              errors={{
                budget: errors.budget,
                targetCpa: errors.targetCpa,
                targetRoas: errors.targetRoas,
                targetImpressionShareLocationFraction: errors.targetImpressionShareLocationFraction,
                targetImpressionShareCpcCeiling: errors.targetImpressionShareCpcCeiling
              }}
            />
          )}

          {currentStep === 4 && formData.campaignType === 'SEARCH' && (
            <NetworkSettingsStep
              networkSettings={formData.networkSettings}
              targetingSettings={formData.targetingSettings}
              onChangeNetworkSettings={(settings) =>
                setFormData((prev) => ({ ...prev, networkSettings: settings }))
              }
              onChangeTargetingSettings={(settings) =>
                setFormData((prev) => ({ ...prev, targetingSettings: settings }))
              }
            />
          )}

          {currentStep === 4 && formData.campaignType === 'SHOPPING' && (
            <ShoppingSettingsStep
              shoppingSettings={formData.shoppingSettings}
              onChangeShoppingSettings={(settings) =>
                setFormData((prev) => ({ ...prev, shoppingSettings: settings }))
              }
              errors={{
                merchantId: errors.merchantId,
                salesCountry: errors.salesCountry,
              }}
            />
          )}

          {currentStep === 4 && formData.campaignType === 'DEMAND_GEN' && (
            <DemandGenSettingsStep
              demandGenSettings={formData.demandGenSettings}
              targetingSettings={formData.targetingSettings}
              onChangeDemandGenSettings={(settings) =>
                setFormData((prev) => ({ ...prev, demandGenSettings: settings }))
              }
              onChangeTargetingSettings={(settings) =>
                setFormData((prev) => ({ ...prev, targetingSettings: settings }))
              }
            />
          )}

          {currentStep === 5 && (
            <ScheduleStep
              startDate={formData.startDate}
              endDate={formData.endDate}
              hasEndDate={formData.hasEndDate}
              onChangeStartDate={(date) => setFormData((prev) => ({ ...prev, startDate: date }))}
              onChangeEndDate={(date) => setFormData((prev) => ({ ...prev, endDate: date }))}
              onToggleEndDate={(enabled) =>
                setFormData((prev) => ({ ...prev, hasEndDate: enabled, endDate: enabled ? prev.endDate : '' }))
              }
              errors={{ startDate: errors.startDate, endDate: errors.endDate }}
            />
          )}

          {currentStep === 6 && (
            <ReviewStep
              selectedAccount={selectedAccount}
              campaignName={formData.campaignName}
              campaignType={formData.campaignType}
              enableAiMax={formData.enableAiMax}
              status={formData.status}
              containsEuPoliticalAdvertising={formData.containsEuPoliticalAdvertising}
              dailyBudget={formData.dailyBudget}
              biddingStrategy={formData.biddingStrategy}
              targetCpaValue={formData.targetCpaValue}
              targetRoasValue={formData.targetRoasValue}
              targetImpressionShareLocation={formData.targetImpressionShareLocation}
              targetImpressionShareLocationFraction={formData.targetImpressionShareLocationFraction}
              targetImpressionShareCpcCeiling={formData.targetImpressionShareCpcCeiling}
              networkSettings={formData.networkSettings}
              targetingSettings={formData.targetingSettings}
              shoppingSettings={formData.shoppingSettings}
              demandGenSettings={formData.demandGenSettings}
              startDate={formData.startDate}
              endDate={formData.endDate}
              hasEndDate={formData.hasEndDate}
              onEdit={handleStepClick}
            />
          )}

          {/* Submit Error */}
          {submitError && (
            <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-md">
              <p className="text-sm text-red-600">{submitError}</p>
            </div>
          )}
        </div>

        {/* Footer with navigation buttons */}
        {/* Desktop layout: Previous | Step | Next */}
        <div className="hidden sm:flex px-6 py-4 border-t border-gray-200 justify-between items-center">
          <Button
            onClick={handlePrevious}
            disabled={currentStep <= previousFloor || isSubmitting}
            variant="secondary"
          >
            Previous
          </Button>

          <div className="text-sm text-gray-500">
            Step {currentStep} of {WIZARD_STEPS.length}
          </div>

          {currentStep < WIZARD_STEPS.length ? (
            <Button onClick={handleNext} disabled={isSubmitting}>
              Next
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={isSubmitting} variant="primary">
              {isSubmitting ? 'Creating Google Ads Campaign...' : 'Create Google Ads Campaign'}
            </Button>
          )}
        </div>

        {/* Mobile layout: Next, Step, Previous (top to bottom, matching tab order) */}
        <div className="flex sm:hidden flex-col px-6 py-4 border-t border-gray-200 items-center gap-3">
          {currentStep < WIZARD_STEPS.length ? (
            <Button onClick={handleNext} disabled={isSubmitting} className="w-full">
              Next
            </Button>
          ) : (
            <Button onClick={handleSubmit} disabled={isSubmitting} variant="primary" className="w-full">
              {isSubmitting ? 'Creating Google Ads Campaign...' : 'Create Google Ads Campaign'}
            </Button>
          )}

          <div className="text-sm text-gray-500">
            Step {currentStep} of {WIZARD_STEPS.length}
          </div>

          <Button
            onClick={handlePrevious}
            disabled={currentStep <= previousFloor || isSubmitting}
            variant="secondary"
            className="w-full"
          >
            Previous
          </Button>
        </div>
      </div>

      {/* Cancel-while-dirty confirmation. Same pattern as
          MetaAdCreation/CreateMetaCampaignWizard.tsx — the wizards stay
          analogous on cancel UX. */}
      <ConfirmDialog
        isOpen={showCancelConfirm}
        onClose={() => setShowCancelConfirm(false)}
        onConfirm={() => {
          setShowCancelConfirm(false);
          onClose();
        }}
        title="Discard campaign?"
        message="You have unsaved changes. Closing now will discard the campaign you've been setting up."
        confirmText="Discard"
        cancelText="Keep editing"
        variant="warning"
      />
    </div>
  );
};
